import { Flow } from '@dynamic-flow/types';
import { ThemeConfig } from '@dynamic-flow/types';
import {
  generateAdvancedCSSVariables,
  generateCSSVariables,
  getTheme,
  resolveTheme,
} from '@dynamic-flow/ui';

const themeCacheKey = (flowId: string) => `dynamic-flow-theme:${flowId}`;

export function cacheFormTheme(flowId: string, theme: ThemeConfig) {
  try {
    sessionStorage.setItem(themeCacheKey(flowId), JSON.stringify(theme));
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

function readCachedTheme(flowId?: string): ThemeConfig | null {
  if (!flowId) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(themeCacheKey(flowId));
    return raw ? (JSON.parse(raw) as ThemeConfig) : null;
  } catch {
    return null;
  }
}

function resolveThemeConfig(
  flow: Flow | null,
  flowId?: string
): ThemeConfig | null {
  return flow?.theme ?? readCachedTheme(flowId);
}

export function getFormPrimaryColor(
  flow: Flow | null,
  flowId?: string
): string {
  const themeConfig = resolveThemeConfig(flow, flowId);
  const advancedPrimary =
    themeConfig?.advanced?.colors?.primary?.baseColor?.hex;
  if (advancedPrimary) {
    return advancedPrimary;
  }

  const theme = themeConfig ? resolveTheme(themeConfig) : getTheme('bh');
  return theme.colors.primary.main;
}

export function buildFormThemeStyles(flow: Flow | null, flowId?: string) {
  const themeConfig = resolveThemeConfig(flow, flowId);
  const theme = themeConfig ? resolveTheme(themeConfig) : getTheme('bh');
  const themeCSS = generateCSSVariables(theme);
  const advancedThemeCSS = themeConfig?.advanced
    ? generateAdvancedCSSVariables(themeConfig.advanced)
    : '';

  return {
    theme,
    primaryColor: getFormPrimaryColor(flow, flowId),
    mergedCSS: `${themeCSS}\n${advancedThemeCSS}`,
    backgroundColor: theme.colors.background.default,
  };
}
